import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, Phone } from "lucide-react";
import { ChapterLabel } from "./ChapterLabel";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "name", label: "Name", type: "text", testid: "contact-input-name", autoComplete: "name" },
  { key: "business_name", label: "Business Name", type: "text", testid: "contact-input-business", autoComplete: "organization" },
  { key: "phone", label: "Phone", type: "tel", testid: "contact-input-phone", autoComplete: "tel" },
  { key: "email", label: "Email", type: "email", testid: "contact-input-email", autoComplete: "email" },
];

const EMPTY = { name: "", business_name: "", phone: "", email: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Request received — we'll be in touch shortly to book your free call.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please email owengraham5546@gmail.com or call 705-433-2050.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact-form" className="py-24 sm:py-32 bg-[#0B162B]/40 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <ChapterLabel text="Chapter 10 // Action" />
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight text-white">
              Ready to Stop Guessing?
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-md">
              Book a free call and see exactly what's happening with your online presence.
            </p>
            <div className="mt-10 space-y-4">
              <a href="mailto:owengraham5546@gmail.com" data-testid="contact-email-link" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors duration-300">
                <Mail className="h-4 w-4" /> owengraham5546@gmail.com
              </a>
              <a href="tel:705-433-2050" data-testid="contact-phone-link" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors duration-300">
                <Phone className="h-4 w-4" /> 705-433-2050
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form-container"
              className="rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-8 sm:p-10 space-y-5"
            >
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label htmlFor={f.key} className="mb-2 block font-mono-label text-xs uppercase tracking-[0.25em] text-slate-400">
                    {f.label}
                  </label>
                  <input
                    id={f.key}
                    data-testid={f.testid}
                    type={f.type}
                    required
                    autoComplete={f.autoComplete}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3.5 text-base text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-white/50 focus:bg-white/[0.07]"
                  />
                </div>
              ))}
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="group mt-2 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-base font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    Sending
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </>
                ) : (
                  <>
                    Book My Free Call
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
